import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import {
  getContractsTableColumns,
  getContractsTableFilters,
} from "routes/entity/components/contracts_table/ContractsTableColumnsAndFilters";
import { CONTRACTS_TABLE_CONSTANTS } from "routes/entity/components/contracts_table/ContractsTableV3";
import { contractKeys } from "routes/entity/components/contracts_table/queryKeys";
import ContractService from "services/Contract";
import UserService from "services/User";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";

const fetchContracts = async ({ entityId = null }) => {
  let contracts = await ContractService.getContractForFilters({
    entity_id: entityId,
  });
  return contracts;
};

const fetchCustomFields = async ({ entityId = null }) => {
  const customFields = await ContractService.getCustomFields({
    entity_id: entityId,
  });
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel("Contract");
  return [
    {
      accessorPrefix: "custom_fields",
      fields: customFields,
      model: "Contract",
      headerPrefix: "Contract",
      configs,
    },
  ];
};

export const useContractsTable = ({ entityId = null }) => {
  const { data: contracts, isSuccess: isContractsFetched } = useQuery({
    queryKey: ["contracts-options", entityId],
    queryFn: () => fetchContracts({ entityId }),
  });
  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["custom-fields", entityId],
    queryFn: () => fetchCustomFields({ entityId }),
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(() => contractKeys.list({ entityId }), [entityId]);

  const invalidateData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      // The contract filter's options are the contracts themselves. The prefix
      // covers both the scoped and unscoped variants of that key.
      queryClient.invalidateQueries({ queryKey: ["contracts-options"] }),
    ]);
  }, [queryKey]);

  const deleteContract = useCallback(
    async (id, entityId) => {
      await ContractService.deleteContract(id, entityId);
      await invalidateData();
    },
    [invalidateData]
  );

  const filters = useMemo(() => {
    if (!isContractsFetched || !isCustomFieldsFetched) {
      return [];
    }
    const finalFilters = getContractsTableFilters({
      contractOptions: contracts,
    });
    if (isCustomFieldsFetched) {
      finalFilters.push(
        ...buildCustomFieldFilters(
          customFields,
          finalFilters.map(f => f.title)
        )
      );
    }
    return finalFilters;
  }, [contracts, customFields, isContractsFetched, isCustomFieldsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getContractsTableColumns({
      entityId,
      deleteContract,
      invalidateData,
    });
    const contractsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CONTRACTS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: contractsTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: contractsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [
    isTableSettingsFetched,
    isCustomFieldsFetched,
    entityId,
    customFields,
    tableSettings,
    invalidateData,
    deleteContract,
  ]);

  return { filters, columns, customFields, queryKey, invalidateData };
};
